import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { exportToExcel } from "../../utils/util";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import useApiEp from "../../hooks/useApiEp";

const CHART_COLORS = ["#7c3aed", "#2f66ee", "#0d8dcc", "#10a979", "#f59e0b", "#ef5da8"];
const cardSx = { height: "100%", border: "1px solid #e5eaf2", borderRadius: 3, boxShadow: "0 8px 24px rgba(20, 36, 66, 0.06)" };

const IndustrySectorwiseCard = () => {
  const { data: sectorwiseData, isLoading: isSectorwiseLoading } = useApiEp(
    `/user/get-industry-sectorwise-registrations`
  );
  const [isFullscreen, setFullscreen] = useState(false);

  // Transform the data to match the format for Recharts
  const dataBySector = useMemo(() => {
    if (!sectorwiseData || !Array.isArray(sectorwiseData.registrations))
      return [];

    return sectorwiseData.registrations
      .filter(
        (item) =>
          typeof item.industrySector === "string" && item.industrySector.trim()
      ) // Keep only valid strings that are not empty
      .map((item) => ({
        industrySector: item.industrySector.trim(),
        count: item.count,
      }));
  }, [sectorwiseData]);

  const handleFullscreenToggle = () => setFullscreen((prev) => !prev);

  // Limit items for compact view
  const displayedData = isFullscreen ? dataBySector : dataBySector.slice(0, 6); // Show top 6 sectors in compact view

  if (isSectorwiseLoading) {
    return (
      <Card sx={cardSx}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            height={250}
          >
            <CircularProgress />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={cardSx}>
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          <Stack direction={"row"} spacing={2} alignItems="center">
            <Typography flex={1} variant="h6" fontWeight={800} color="#172033">
              Registrations by Industry Sector
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() =>
                exportToExcel(
                  dataBySector.map(({ industrySector, count }) => ({
                    industrySector,
                    count,
                  })),
                  "Registrations_by_Industry_Sector"
                )
              }
            >
              Export
            </Button>
            <IconButton onClick={handleFullscreenToggle}>
              <FullscreenIcon />
            </IconButton>
          </Stack>
          <ResponsiveContainer width="100%" height={285}>
            <BarChart data={displayedData} layout="vertical" margin={{ right: 45 }}>
              <CartesianGrid stroke="#edf1f7" strokeDasharray="4 4" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#8995a8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="industrySector" width={200} tick={{ fill: "#536176", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#f5f7fb" }} contentStyle={{ border: "1px solid #e5eaf2", borderRadius: 12, boxShadow: "0 10px 28px rgba(20, 36, 66, 0.12)" }} />
              <Legend iconType="circle" wrapperStyle={{ color: "#6d7b91", fontSize: 13 }} />
              <Bar dataKey="count" name="Registrations" radius={[0, 7, 7, 0]} maxBarSize={30}>
                {displayedData.map((entry, index) => <Cell key={entry.industrySector} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                <LabelList dataKey="count" position="right" fill="#536176" fontWeight={700} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Fullscreen Chart Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={handleFullscreenToggle}
        fullWidth
        maxWidth="lg"
      >
        <DialogContent
          sx={{
            maxHeight: "80vh", // Limits the height of the dialog content
            overflowY: "auto", // Enables vertical scrolling for taller content
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Full-Screen Registrations by Industry Sector
            </Typography>
            <IconButton onClick={handleFullscreenToggle}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Dynamically calculate chart height based on number of sectors */}
          <ResponsiveContainer
            width="100%"
            height={dataBySector.length * 50} // Adjust height dynamically, 50px per bar
          >
            <BarChart data={dataBySector} layout="vertical" margin={{ right: 50 }}>
              <CartesianGrid stroke="#edf1f7" strokeDasharray="4 4" horizontal={false} />
              <XAxis type="number" axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="industrySector"
                width={200} // Adjusted for potentially longer industry sector names
              />
              <Tooltip />
              <Legend iconType="circle" />
              <Bar dataKey="count" name="Registrations" radius={[0, 7, 7, 0]} maxBarSize={30}>
                {dataBySector.map((entry, index) => <Cell key={entry.industrySector} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                <LabelList dataKey="count" position="right" fill="#536176" fontWeight={700} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IndustrySectorwiseCard;
